import { getReviewsForProduct } from "@faire/prototype-playground/data/entity_sets/reviewsDatabase";
import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

import {
  PADX,
  RAIL_BLEED_RIGHT,
  RAIL_PEEK,
  TILE_HOVER_GROUP,
  TILE_HOVER_IMAGE,
} from "../constants";
import { type ProductCardData } from "../ProductCard";
import { pickBestReview } from "../reviewQuality";
import { SectionHeader } from "../SectionHeader";

interface HighlyRatedProps {
  /** Real brand products (with images) to feature. Only the first 4 are shown. */
  products: ProductCardData[];
  /** Where the "Shop all products" CTA routes (omit to hide it). */
  onShopAll?: () => void;
  /** See `BrandDataset.productImageAspect`. */
  aspectRatio?: "square" | "portrait";
  /**
   * Hide the product name under the reviewer. The quote and reviewer stay;
   * the photo still carries the product via `alt`. Default keeps the name so
   * marketplace homes don't change.
   */
  hideProductName?: boolean;
}

/**
 * "Highly rated" — four product tiles, each a photo with a glowing 5-star quote
 * hung between the photo and the product name. Price is intentionally omitted to
 * keep the focus on the product + the social proof.
 */
export const HighlyRated: React.FC<HighlyRatedProps> = ({
  products,
  onShopAll,
  aspectRatio,
  hideProductName = false,
}) => {
  // Each tile pairs a real product with a real, positive review of THAT
  // product (not a generic brand-agnostic quote) — a product with no real
  // review imported yet is skipped rather than shown with a mismatched or
  // fabricated one. `pickBestReview` chooses between a product's reviews when
  // it has several, and returns nothing when they are all too long to sit in
  // a tile; see reviewQuality.ts.
  const cards = products
    .filter((product) => product.imageUrl)
    .map((product) => ({
      card: {
        token: product.token,
        name: product.name,
        imageUrl: product.imageUrl,
        isBestseller: true,
      },
      review: pickBestReview(getReviewsForProduct(product.token)),
    }))
    .filter(
      (
        entry
      ): entry is {
        card: (typeof entry)["card"];
        review: NonNullable<(typeof entry)["review"]>;
      } => entry.review !== undefined
    )
    .slice(0, 4);

  // pb-16 (64px): the quote/reviewer/product-name stack under each tile made
  // the old pb-4 read as cramped against the next module (Ryan, 2026-08-13).
  return (
    <div className={cn("pt-8 pb-16", PADX)}>
      <SectionHeader
        title="Highly rated"
        showArrows={false}
        showSeeAll={onShopAll !== undefined}
        seeAllVariant="button"
        seeAllLabel="Shop all products"
        onSeeAll={onShopAll}
      />
      {/* A rail below `md`, the 4-up grid from `md`. Stacked two-up, each tile
          carried a photo plus a three-line quote, and four of them became a
          wall the reader scrolls past rather than reads. One tile plus the
          same 48px peek Behind the Scenes uses puts a single quote in front
          of them at a size worth reading, and the sliver says there are more
          — matching peeks so the two rails read as one system, not two
          different guesses at how much to show (Ryan, 2026-09-22). */}
      <div
        className={cn(
          "flex snap-x snap-mandatory gap-x-[var(--grid-gutter,16px)] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          RAIL_BLEED_RIGHT,
          "md:grid md:snap-none md:grid-cols-4 md:gap-y-8 md:overflow-x-visible"
        )}
        style={{ "--rail-peek": RAIL_PEEK } as React.CSSProperties}
      >
        {cards.map(({ card, review }) => (
          <div
            key={card.token}
            className={cn(
              "flex min-w-0 flex-col gap-3",
              "w-[calc(100%-var(--rail-peek,48px))] shrink-0 snap-start",
              "md:w-auto"
            )}
          >
            {/* Product photo (sign-up gated) */}
            <button
              type="button"
              data-signup-gate
              className={cn(
                "bg-fs-surface-secondary relative block w-full overflow-hidden rounded",
                TILE_HOVER_GROUP,
                aspectRatio === "portrait" ? "aspect-[2/3]" : "aspect-square"
              )}
            >
              {card.imageUrl ? (
                <img
                  src={card.imageUrl}
                  alt={card.name}
                  className={cn("h-full w-full object-cover", TILE_HOVER_IMAGE)}
                />
              ) : null}
              {/* Same faint scrim as ProductCard, so white-background photos
                  keep an edge against the page's white. */}
              {card.imageUrl ? (
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{ backgroundColor: "rgba(0,0,0,0.025)" }}
                />
              ) : null}
              {card.isBestseller ? (
                <span className="bg-fs-surface-primary type-fs-label text-fs-text-primary absolute top-2 left-2 rounded-[4px] px-2 py-1">
                  Bestseller
                </span>
              ) : null}
            </button>
            {/* Quote, then reviewer + product name (name sits below the reviewer
              in the same subdued style).

              Hanging punctuation, done the way it has to be done in Chrome:
              the real `hanging-punctuation: first` is Safari-only, so the
              opening quote is pulled out with a negative text-indent and the
              stack is padded by the same amount to catch it. Without the
              padding the mark hangs outside the tile — into the page margin in
              the first column and into the grid gap in the rest. The padding
              is on the stack rather than the quote alone so all three lines
              share one text column and only the glyph sits outside it. */}
            <div
              className="flex flex-col gap-1.5"
              style={{ paddingLeft: "0.5em" }}
            >
              <p
                className="type-fs-paragraph text-fs-text-primary"
                style={{ textIndent: "-0.5em" }}
              >
                &ldquo;{review.reviewText}&rdquo;
              </p>
              <span className="type-fs-label text-fs-text-subdued leading-tight">
                {review.reviewerName}
                {review.reviewerLocation ? ` · ${review.reviewerLocation}` : ""}
              </span>
              {hideProductName ? null : (
                <span className="type-fs-label text-fs-text-subdued leading-tight">
                  {card.name}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
